<template>
  <div class="admin-layout">
    <!-- Side Navigation Bar -->
    <Navbar/>

    <!-- Main Content Area -->
    <div class="admin-content">
      <header class="admin-header">
        <h1>Order Management</h1>
        <div class="user-profile">
          <span>Welcome, Admin</span>
          <i class="fas fa-user-circle"></i>
        </div>
      </header>

      <main class="dashboard">
        <!-- Stats Cards -->
        <section class="stats-cards">
          <div class="stat-card">
            <div class="stat-icon"><i class="fas fa-box"></i></div>
            <div class="stat-info">
              <h3>{{ orders.length }}</h3>
              <p>Total Orders</p>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon"><i class="fas fa-tags"></i></div>
            <div class="stat-info">
              <h3>${{ sumPrice.toFixed(2) }}</h3>
              <p>Revenue</p>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon"><i class="fas fa-dollar-sign"></i></div>
            <div class="stat-info">
              <h3>${{ avgPrice.toFixed(2) }}</h3>
              <p>Avg. Price</p>
            </div>
          </div>

        </section>

        <!-- Product Table -->
        <section class="product-section">
          <div class="product-header">
            <h2>Orders List</h2>
            <div class="search-bar">
              <input type="text" v-model="searchTerm" placeholder="Search with discord and email...">
              <i class="fas fa-search"></i>
            </div>
          </div>

          <div class="product-table">
            <table class="min-w-full table-auto border border-gray-300">
              <thead>
              <tr class="bg-gray-200 text-left">
                <th class="px-4 py-2">#</th>
                <th class="px-4 py-2">Email</th>
                <th class="px-4 py-2">Discord Name</th>
                <th class="px-4 py-2">Coordinates</th>
                <th class="px-4 py-2">Price</th>
                <th class="px-4 py-2">Description</th>
                <th class="px-4 py-2">Stage</th>
                <th class="px-4 py-2">Payment Method</th>
                <th class="px-4 py-2">Kits</th>
                <th class="px-4 py-2">Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr
                  v-for="(order, index) in filteredProducts"
                  :key="index"
                  class="border-t border-gray-300 hover:bg-gray-50"
              >
                <td class="px-4 py-2">{{ index + 1 }}</td>
                <td class="px-4 py-2">{{ order.email }}</td>
                <td class="px-4 py-2">{{ order.discordName }}</td>
                <td class="px-4 py-2">{{ order.coordinates || '—' }}</td>
                <td class="px-4 py-2">${{ order.price }}</td>
                <td class="px-4 py-2">{{ truncateText(order.description, 50) }}</td>
                <td class="px-4 py-2">
                  {{ order.stage == 'sent' ? 'Waiting Payment Confirmation' : order.stage == 'payment' ? 'Payment Confirmed' : 'Delivered' }}
                </td>
                <td class="px-4 py-2">{{ order.paymentMethod }}</td>
                <td class="px-4 py-2">
                  <button
                      @click="openKitModal(order.kits)"
                      class="btn-see">
                    <i class="fas fa-eye"></i>
                  </button>
                </td>

                <td class="action-buttons">
                  <button @click="openCoordinateModal(order, index)" class="btn-edit">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button @click="confirmDelete(index)" class="btn-delete">
                    <i class="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>

    <div v-if="showKitModal" class="kit-modal-overlay">
      <div class="kit-modal">
        <div class="kit-modal-header">
          <h2><i class="fas fa-box-open"></i> Kit List</h2>
          <button @click="closeKitModal" class="close-button">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="kit-modal-content">
          <ul v-if="groupedKits && Object.keys(groupedKits).length > 0" class="kit-list">
            <li v-for="(count, kit) in groupedKits" :key="kit" class="kit-item">
              <div class="kit-info">
                <i class="fas fa-cube"></i>
                <span>{{ kit }} {{ count }}x</span>
              </div>
            </li>
          </ul>
          <p v-else class="empty-message">No kits available</p>
        </div>

        <div class="kit-modal-footer">
          <button @click="closeKitModal" class="close-kit-button">
            <i class="fas fa-check"></i> Close
          </button>
        </div>
      </div>
    </div>

    <div v-if="showCoordinateModal" class="coordinate-modal-overlay">
      <div class="coordinate-modal">
        <div class="coordinate-modal-header">
          <h2><i class="fas fa-map-marker-alt"></i> Add Coordinates</h2>
          <button @click="closeCoordinateModal" class="close-button">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="coordinate-modal-content">
          <div class="user-info">
            <span class="user-email">{{ selectedOrder.email }}</span>
            <span class="user-discord">{{ selectedOrder.discordName }}</span>
          </div>

          <div class="coordinate-form">
            <div class="coordinate-input-group">
              <label for="x-coordinate">X Coordinate</label>
              <div class="input-with-icon">
                <i class="fas fa-arrows-alt-h"></i>
                <input
                    type="number"
                    id="x-coordinate"
                    v-model="coordinateForm.x"
                    placeholder="Enter X coordinate"
                >
              </div>
            </div>

            <div class="coordinate-input-group">
              <label for="y-coordinate">Y Coordinate</label>
              <div class="input-with-icon">
                <i class="fas fa-arrows-alt-v"></i>
                <input
                    type="number"
                    id="y-coordinate"
                    v-model="coordinateForm.y"
                    placeholder="Enter Y coordinate"
                >
              </div>
            </div>

            <div class="coordinate-input-group">
              <label for="z-coordinate">Z Coordinate</label>
              <div class="input-with-icon">
                <i class="fas fa-cube"></i>
                <input
                    type="number"
                    id="z-coordinate"
                    v-model="coordinateForm.z"
                    placeholder="Enter Z coordinate"
                >
              </div>
            </div>

            <div class="coordinate-input-group">
              <label for="world-select">World</label>
              <div class="select-with-icon">
                <i class="fas fa-globe"></i>
                <select id="world-select" v-model="coordinateForm.world">
                  <option value="overworld">Overworld</option>
                  <option value="nether">Nether</option>
                  <option value="end">The End</option>
                </select>
                <i class="fas fa-chevron-down select-arrow"></i>
              </div>
            </div>
          </div>

          <div class="coordinate-preview">
            <h3>Preview</h3>
            <div class="preview-box">
              {{ formatCoordinates(coordinateForm) }}
            </div>
          </div>
        </div>

        <div class="coordinate-modal-footer">
          <button @click="closeCoordinateModal" class="cancel-button">
            <i class="fas fa-times"></i> Cancel
          </button>
          <button @click="saveCoordinates" class="save-button" :disabled="!isValidCoordinates">
            <i class="fas fa-save"></i> Save Coordinates
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal" v-if="showDeleteModal">
      <div class="modal-content delete-modal">
        <div class="modal-header">
          <h3>Confirm Delete</h3>
          <button @click="showDeleteModal = false" class="close-modal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete the product "<strong>{{ filteredProducts[deleteIndex]?.name }}</strong>"?</p>
          <p class="warning-text">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button @click="showDeleteModal = false" class="btn-cancel">Cancel</button>
          <button @click="deleteProduct" class="btn-delete">Delete</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {collection, getDocs, orderBy, query, Timestamp, deleteDoc, doc, updateDoc} from 'firebase/firestore'
import Navbar from '../Header/Navbar.vue';
import {db} from "@/firebase";

export default {
  name: 'AdminProducts',
  components: {
    Navbar
  },
  data() {
    return {
      orders: [],
      editingOrder: {
        email: '',
        discordName: '',
        coordinates: '',
        stage: '',
        description: '',
        price: 0,
        paymentMethod: '',
        paymentId: '',
        kits: [],
        timestamp: Timestamp.now()
      },
      categoryInput: '',
      editCategoryInput: '',
      searchTerm: '',
      showAddModal: false,
      showCoordinateModal: false,
      selectedOrder: {},
      selectedOrderIndex: -1,
      coordinateForm: {
        x: '',
        y: '',
        z: '',
        world: ''
      },
      selectedKits: [],
      showKitModal: false,
      showDeleteModal: false,
      deleteIndex: -1,
    };
  },
  computed: {
    filteredProducts() {
      if (!this.searchTerm) return this.orders;

      const term = this.searchTerm.toLowerCase();
      return this.orders.filter(order =>
          order.discordName.toLowerCase().includes(term) ||
          order.email.toLowerCase().includes(term)
      );
    },
    isValidCoordinates() {
      return this.coordinateForm.x !== '' && this.coordinateForm.y !== '';
    },
    avgPrice() {
      if (this.orders.length === 0) return 0;
      const total = this.orders.reduce((sum, product) => sum + parseFloat(product.price), 0);
      return total / this.orders.length;
    },
    sumPrice() {
      if (this.orders.length === 0) return 0;
      const total = this.orders.reduce((sum, product) => sum + parseFloat(product.price), 0);
      return total;
    },
    groupedKits() {
      if (!this.selectedKits || this.selectedKits.length === 0) return {};

      return this.selectedKits.reduce((acc, kit) => {
        acc[kit] = (acc[kit] || 0) + 1;
        return acc;
      }, {});
    },
  },
  metaInfo() {
    return {
      title: "Order Management | ShopT"
    };
  },
  async created() {
    await this.fetchKits()
  },
  methods: {
    openKitModal(kits) {
      console.log(kits)
      this.selectedKits = kits;
      this.showKitModal = true;
    },
    closeKitModal() {
      this.showKitModal = false;
      this.selectedKits = [];
    },
    async fetchKits() {
      try {
        const q = query(collection(db, 'payments'), orderBy('timestamp', 'desc'))
        const snapshot = await getDocs(q)
        this.orders = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          quantity: 1,
        }))
      } catch (error) {
        console.error('Kits çekilemedi:', error)
      }
    },
    truncateText(text, length) {
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },
    confirmDelete(index) {
      this.deleteIndex = index;
      this.showDeleteModal = true;
    },
    async deleteProduct() {
      if (this.deleteIndex > -1) {
        try {
          const productId = this.orders[this.deleteIndex].id;
          await deleteDoc(doc(db, 'payments', productId));
          this.orders.splice(this.deleteIndex, 1);
          this.showDeleteModal = false;
          this.deleteIndex = -1;
        } catch (error) {
          console.error('Ürün silinemedi:', error);
        }
      }
    },

    openCoordinateModal(order, index) {
      this.selectedOrder = { ...order };
      this.selectedOrderIndex = index;

      if (order.coordinates) {
        const coords = this.parseCoordinates(order.coordinates);
        this.coordinateForm.x = coords.x || '';
        this.coordinateForm.y = coords.y || '';
        this.coordinateForm.z = coords.z || '';
      } else {
        this.coordinateForm.x = '';
        this.coordinateForm.y = '';
        this.coordinateForm.z = '';
      }

      this.showCoordinateModal = true;
    },

    closeCoordinateModal() {
      this.showCoordinateModal = false;
      this.selectedOrder = {};
      this.selectedOrderIndex = -1;
      this.coordinateForm = { x: '', y: '', z: '' };
    },

    async saveCoordinates() {
      if (!this.isValidCoordinates) return;

      try {
        const formattedCoordinates = this.formatCoordinates(this.coordinateForm);

        const orderRef = doc(db, 'payments', this.selectedOrder.id);
        await updateDoc(orderRef, {
          coordinates: formattedCoordinates,
          stage: "successful"
        });

        this.orders[this.selectedOrderIndex].coordinates = formattedCoordinates;

        this.closeCoordinateModal();

      } catch (error) {
        console.error('Error updating coordinates:', error);
      }
    },

    formatCoordinates(coords) {
      let formatted = `X: ${coords.x}, Y: ${coords.y}, Z: ${coords.z}, ${coords.world}`;
      return formatted;
    },

    parseCoordinates(coordString) {
      const result = { x: '', y: '', z: '' };

      if (!coordString) return result;

      const xMatch = coordString.match(/X:\s*(-?\d+)/i);
      if (xMatch && xMatch[1]) result.x = parseInt(xMatch[1]);

      const yMatch = coordString.match(/Y:\s*(-?\d+)/i);
      if (yMatch && yMatch[1]) result.y = parseInt(yMatch[1]);

      const zMatch = coordString.match(/Z:\s*(-?\d+)/i);
      if (zMatch && zMatch[1]) result.z = parseInt(zMatch[1]);

      return result;
    }
  }
};
</script>

<style scoped>
.world-select-group {
  margin-bottom: 15px;
}

.select-with-icon {
  position: relative;
}

.select-with-icon i:not(.select-arrow) {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9d4edd;
  z-index: 1;
}

.select-with-icon select {
  width: 100%;
  padding: 10px 30px 10px 35px;
  appearance: none;
  -webkit-appearance: none;
  background-color: #333;
  border: 1px solid #444;
  border-radius: 6px;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.select-with-icon select:focus {
  outline: none;
  border-color: #8a2be2;
  box-shadow: 0 0 0 1px #8a2be2;
}

.select-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
}

.admin-layout {
  display: flex;
  min-height: 100vh;
  background-color: #111;
  color: #fff;
}

.btn-edit {
  background-color: rgba(241, 196, 15, 0.2);
}

.btn-edit:hover {
  background-color: rgba(241, 196, 15, 0.4);
  color: #f1c40f;
}

.btn-delete {
  background-color: rgba(231, 76, 60, 0.2);
}

.btn-delete:hover {
  background-color: rgba(231, 76, 60, 0.4);
  color: #e74c3c;
}

/* Main Content Styles */
.admin-content {
  flex: 1;
  padding: 0;
  overflow-y: auto;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  background-color: #111;
  border-bottom: 1px solid #333;
}

.admin-header h1 {
  font-size: 1.8rem;
  font-weight: 500;
  margin: 0;
}

.user-profile {
  display: flex;
  align-items: center;
}

.user-profile span {
  margin-right: 10px;
  font-weight: 500;
}

.user-profile i {
  font-size: 1.5rem;
  color: #ccc;
}

/* Dashboard Styles */
.dashboard {
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 10px;
}

.stat-card {
  background-color: #222;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  align-items: center;
  border: 1px solid #444;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  background-color: purple;
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.stat-icon i {
  font-size: 1.5rem;
  color: #fff;
}

.stat-info h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: #fff;
}

.stat-info p {
  font-size: 0.9rem;
  color: #999;
  margin: 5px 0 0 0;
}

/* Product Section */
.product-section {
  background-color: #222;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #444;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.product-header h2 {
  font-size: 1.5rem;
  margin: 0;
  font-weight: 500;
}

.search-bar {
  position: relative;
  width: 300px;
}

.search-bar input {
  width: 100%;
  padding: 10px 15px;
  padding-right: 40px;
  border-radius: 5px;
  border: 1px solid #444;
  background-color: #333;
  color: #fff;
}

.search-bar i {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #777;
}

.product-table {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background-color: #333;
}

th, td {
  padding: 12px 15px;
  text-align: left;
}

th {
  font-weight: 500;
  color: #ccc;
}

tbody tr {
  border-bottom: 1px solid #444;
}

tbody tr:last-child {
  border-bottom: none;
}

.product-image img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 5px;
}

.category-tag i {
  margin-left: 5px;
  cursor: pointer;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-buttons button {
  border: none;
  background: none;
  color: #ccc;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-see {
  display: flex;
  gap: 8px;
  border: none;
  background: none;
  color: #ccc;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-edit {
  background-color: rgba(241, 196, 15, 0.2);
}

.btn-edit:hover {
  background-color: rgba(241, 196, 15, 0.4);
  color: #f1c40f;
}

.btn-delete {
  background-color: rgba(231, 76, 60, 0.2);
}

/* Kit Modal Overlay */
.kit-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  backdrop-filter: blur(3px);
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Kit Modal Container */
.kit-modal {
  background-color: #222;
  border-radius: 10px;
  width: 90%;
  max-width: 450px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  border: 1px solid #8a2be2;
  animation: slideIn 0.3s ease-out;
  overflow: hidden;
}

@keyframes slideIn {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

/* Modal Header */
.kit-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #444;
  background-color: #2a2a2a;
}

.kit-modal-header h2 {
  margin: 0;
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.kit-modal-header h2 i {
  color: #9d4edd;
  margin-right: 10px;
  font-size: 18px;
}

.close-button {
  background: none;
  border: none;
  color: #888;
  font-size: 16px;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}

/* Modal Content */
.kit-modal-content {
  padding: 20px;
  max-height: 300px;
  overflow-y: auto;
}

/* Custom Scrollbar */
.kit-modal-content::-webkit-scrollbar {
  width: 6px;
}

.kit-modal-content::-webkit-scrollbar-track {
  background: #2d2d2d;
  border-radius: 10px;
}

.kit-modal-content::-webkit-scrollbar-thumb {
  background: #8a2be2;
  border-radius: 10px;
}

.kit-modal-content::-webkit-scrollbar-thumb:hover {
  background: #9d4edd;
}

/* Kit List */
.kit-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.kit-item {
  background-color: #333;
  border-radius: 6px;
  padding: 12px 15px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  color: #e0e0e0;
  transition: all 0.2s;
}

.kit-item:hover {
  background-color: #444;
  transform: translateX(2px);
}

.kit-item i {
  color: #9d4edd;
  margin-right: 12px;
  font-size: 14px;
}

.empty-message {
  color: #888;
  text-align: center;
  font-style: italic;
  padding: 20px 0;
}

/* Modal Footer */
.kit-modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #444;
  display: flex;
  justify-content: flex-end;
}

.close-kit-button {
  background-color: #8a2be2;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s;
}

.close-kit-button:hover {
  background-color: #9d4edd;
  box-shadow: 0 2px 8px rgba(138, 43, 226, 0.4);
}

.close-kit-button i {
  margin-right: 8px;
}

.btn-delete:hover {
  background-color: rgba(231, 76, 60, 0.4);
  color: #e74c3c;
}

/* Modal Styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: #222;
  border-radius: 10px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.delete-modal {
  max-width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #444;
}

.modal-header h3 {
  margin: 0;
  font-weight: 500;
}

.close-modal {
  background: none;
  border: none;
  color: #ccc;
  font-size: 1.2rem;
  cursor: pointer;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #444;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #444;
  background-color: #333;
  color: #fff;
}

.form-group textarea {
  min-height: 100px;
  resize: vertical;
}

.warning-text {
  color: #e74c3c;
  font-style: italic;
}

.btn-cancel,
.btn-delete {
  padding: 8px 15px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  font-weight: 500;
}

.btn-cancel {
  background-color: #555;
  color: #fff;
}

.btn-delete {
  background-color: #e74c3c;
  color: #fff;
}

@media (max-width: 768px) {
  .admin-layout {
    flex-direction: column;
  }

  .stats-cards {
    grid-template-columns: 1fr;
  }

  .product-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .search-bar {
    width: 100%;
  }

  .dashboard {
    padding: 15px;
  }
}

.coordinate-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  backdrop-filter: blur(3px);
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Coordinate Modal Container */
.coordinate-modal {
  background-color: #222;
  border-radius: 10px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  border: 1px solid #8a2be2;
  animation: slideIn 0.3s ease-out;
  overflow: hidden;
}

@keyframes slideIn {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

/* Modal Header */
.coordinate-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #444;
  background-color: #2a2a2a;
}

.coordinate-modal-header h2 {
  margin: 0;
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.coordinate-modal-header h2 i {
  color: #9d4edd;
  margin-right: 10px;
  font-size: 18px;
}

.close-button {
  background: none;
  border: none;
  color: #888;
  font-size: 16px;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}

/* Modal Content */
.coordinate-modal-content {
  padding: 20px;
}

/* User Info */
.user-info {
  background-color: #2a2a2a;
  border-radius: 6px;
  padding: 12px 15px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.user-email {
  color: #fff;
  font-weight: 500;
}

.user-discord {
  color: #9d4edd;
  font-weight: 500;
}

/* Coordinate Form */
.coordinate-form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.coordinate-input-group {
  display: flex;
  flex-direction: column;
}

.coordinate-input-group label {
  color: #ccc;
  margin-bottom: 6px;
  font-size: 14px;
}

.input-with-icon {
  position: relative;
}

.input-with-icon i {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9d4edd;
}

.input-with-icon input {
  width: 100%;
  padding: 10px 10px 10px 35px;
  background-color: #333;
  border: 1px solid #444;
  border-radius: 6px;
  color: #fff;
  font-size: 16px;
  transition: all 0.2s;
}

.input-with-icon input:focus {
  outline: none;
  border-color: #8a2be2;
  box-shadow: 0 0 0 1px #8a2be2;
}

/* Coordinate Preview */
.coordinate-preview {
  background-color: #2a2a2a;
  border-radius: 6px;
  padding: 12px 15px;
  margin-bottom: 10px;
}

.coordinate-preview h3 {
  margin: 0 0 10px 0;
  font-size: 16px;
  color: #ccc;
}

.preview-box {
  background-color: #333;
  border: 1px solid #444;
  border-radius: 6px;
  padding: 12px;
  font-family: 'Courier New', monospace;
  color: #9d4edd;
  font-weight: bold;
  text-align: center;
  font-size: 18px;
}

/* Modal Footer */
.coordinate-modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #444;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.cancel-button {
  background-color: #444;
  color: #ccc;
  border: none;
  border-radius: 6px;
  padding: 10px 15px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s;
}

.cancel-button:hover {
  background-color: #555;
  color: #fff;
}

.save-button {
  background-color: #8a2be2;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s;
}

.save-button:hover {
  background-color: #9d4edd;
  box-shadow: 0 2px 8px rgba(138, 43, 226, 0.4);
}

.save-button:disabled {
  background-color: #555;
  color: #888;
  cursor: not-allowed;
  box-shadow: none;
}

.save-button i, .cancel-button i {
  margin-right: 8px;
}
</style>